import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, SearchIcon, XIcon } from 'lucide-react';
import { Logo } from '../brand/Logo';
import { Button } from '../ui/Button';
import { SERVICE_LINES } from '../../data/services';
const INSIGHT_LINKS = [{
  to: '/insights',
  label: 'Articles',
  detail: 'Analysis by sector and service line'
}, {
  to: '/resources',
  label: 'Resources',
  detail: 'eBooks, reports and checklists'
}, {
  to: '/assessment',
  label: 'AI readiness assessment',
  detail: 'Score your organisation in four minutes'
}];
export function Header() {
  const [scrolled, setScrolled] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState<'services' | 'insights' | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  React.useEffect(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setDrawerOpen(false);
        setSearchOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  React.useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchOpen(false);
    navigate('/insights');
    setQuery('');
  };
  const triggerClass = (menu: 'services' | 'insights') => `flex items-center gap-1.5 border-b-2 pb-1 text-[13px] font-semibold uppercase tracking-[0.1em] transition-colors ${openMenu === menu ? 'border-gold text-white' : 'border-transparent text-white/80 hover:text-white'}`;
  return <header className={`sticky top-0 z-40 w-full bg-ink transition-all duration-200 ${scrolled ? 'py-2.5 shadow-[0_1px_0_rgba(255,255,255,0.1)]' : 'py-4'}`} data-id="element-157" data-mp="2mnc7m36gfcxt">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:text-ink" data-id="element-158" data-mp="2vnqwtdmjhc6l">
        Skip to content
      </a>

      <div className="mx-auto flex w-full max-w-[1240px] items-center gap-8 px-5 sm:px-8" data-id="element-159" data-mp="22zl8kgwa1520">
        <Link to="/" aria-label="Cavalla Solution, home" className="shrink-0" data-id="element-160" data-mp="ig96xyrfrq8k">
          <Logo variant="reversed" data-id="element-161" data-mp="n7hgcusrnblq" />
        </Link>

        <div ref={navRef} className="hidden items-center gap-8 lg:flex" data-id="element-162" data-mp="n929cfzfmmhh">
          <NavLink to="/about" className={({
          isActive
        }) => `border-b-2 pb-1 text-[13px] font-semibold uppercase tracking-[0.1em] transition-colors ${isActive ? 'border-gold text-white' : 'border-transparent text-white/80 hover:text-white'}`} data-id="element-163" data-mp="9gvucx80mljm">
            About us
          </NavLink>

          <div className="relative" data-id="element-164" data-mp="s6u8nv2epavp">
            <button type="button" aria-expanded={openMenu === 'services'} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === 'services' ? null : 'services')} className={triggerClass('services')} data-id="element-165" data-mp="s5snuyvl9a0s">
              Services
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${openMenu === 'services' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-166" data-mp="3hx7ewuv93fj5" />
            </button>
            {openMenu === 'services' && <div className="absolute left-0 top-full z-50 mt-4 w-[340px] border border-line bg-white p-2 shadow-2xl" data-id="element-167" data-mp="3odh9aq58lm6x">
                <Link to="/services" className="block px-4 py-2.5 text-[15px] font-medium text-ink hover:bg-mist" data-id="element-168" data-mp="3jcbi4wr866zi">
                  All services
                </Link>
                {SERVICE_LINES.map(line => <Link key={line.id} to={`/services/${line.id}`} className="block px-4 py-2.5 hover:bg-mist" data-id="element-169" data-mp="ay2wk8n4eggu">
                    <span className="block text-[15px] font-medium text-ink" data-id="element-170" data-mp="601arfyb831o">{line.name}</span>
                    <span className="mt-0.5 block text-small text-muted" data-id="element-171" data-mp="2vp3cgdpvwvwy">{line.short}</span>
                  </Link>)}
                <Link to="/government" className="block px-4 py-2.5 hover:bg-mist" data-id="element-172" data-mp="2qnw34a83foo6">
                  <span className="block text-[15px] font-medium text-ink" data-id="element-173" data-mp="2gmlgn8a9zvlw">Cavalla Public Delivery</span>
                  <span className="mt-0.5 block text-small text-muted" data-id="element-174" data-mp="2lleu50pkbmm2">
                    National development plan delivery, for government
                  </span>
                </Link>
                <Link to="/augmented-intelligence" className="mt-1 block border-t border-line px-4 py-2.5 pt-3.5 text-[15px] text-body hover:bg-mist" data-id="element-175" data-mp="2vbhtdina1wsu">
                  Our method
                </Link>
              </div>}
          </div>

          <div className="relative" data-id="element-176" data-mp="39bivt2j6xcaf">
            <button type="button" aria-expanded={openMenu === 'insights'} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === 'insights' ? null : 'insights')} className={triggerClass('insights')} data-id="element-177" data-mp="3984vs0y01f65">
              Insights
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${openMenu === 'insights' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-178" data-mp="1bulkj5k9wzgu" />
            </button>
            {openMenu === 'insights' && <div className="absolute left-0 top-full z-50 mt-4 w-[340px] border border-line bg-white p-2 shadow-2xl" data-id="element-179" data-mp="1gtohs3u0i1e9">
                {INSIGHT_LINKS.map(link => <Link key={link.to} to={link.to} className="block px-4 py-2.5 hover:bg-mist" data-id="element-180" data-mp="1urczj2ss8sf3">
                    <span className="block text-[15px] font-medium text-ink" data-id="element-181" data-mp="1pu34n7mmjojv">{link.label}</span>
                    <span className="mt-0.5 block text-small text-muted" data-id="element-182" data-mp="24ihcxy2t857t">{link.detail}</span>
                  </Link>)}
              </div>}
          </div>
        </div>

        <div className="ml-auto hidden items-center gap-5 lg:flex" data-id="element-183" data-mp="2dk75j78fha76">
          {searchOpen ? <form onSubmit={onSearch} className="flex items-center gap-2" data-id="element-184" data-mp="29k6rhnp8r10n">
              <label htmlFor="site-search" className="sr-only" data-id="element-185" data-mp="2s7pe4gp42oyv">
                Search insights and resources
              </label>
              <input id="site-search" type="search" autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Search insights" className="w-48 border-b border-white/40 bg-transparent px-1 py-1.5 text-[15px] text-white placeholder:text-white/45" data-id="element-186" data-mp="2x3mtoksjvral" />
              <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search" data-id="element-187" data-mp="2e8y3j49gjlfh">
                <XIcon className="h-5 w-5 text-white/70" aria-hidden="true" data-id="element-188" data-mp="20ddkm8tqb382" />
              </button>
            </form> : <button type="button" onClick={() => setSearchOpen(true)} aria-label="Open search" data-id="element-189" data-mp="1qgqyzhtkw84z">
              <SearchIcon className="h-5 w-5 text-white transition-colors hover:text-gold" aria-hidden="true" data-id="element-190" data-mp="1vg3u0pulslin" />
            </button>}

          <Button to="/contact" size="sm" shape="pill" className="px-5 py-2.5" data-id="element-191" data-mp="2axv6g91k53xr">
            Book a discovery call
          </Button>
          <Link to="/portal/login" className="text-[15px] text-white underline underline-offset-4 transition-colors hover:text-gold" data-id="element-192" data-mp="25xh497mn0o7n">
            Client login
          </Link>
        </div>

        <button type="button" className="ml-auto lg:hidden" aria-expanded={drawerOpen} aria-controls="mobile-nav" aria-label={drawerOpen ? 'Close menu' : 'Open menu'} onClick={() => setDrawerOpen(open => !open)} data-id="element-193" data-mp="1s02f840aqig7">
          {drawerOpen ? <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-194" data-mp="1e8xcgt1mta1n" /> : <MenuIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-195" data-mp="199kmw0jdlovd" />}
        </button>
      </div>

      {drawerOpen && <div id="mobile-nav" className="fixed inset-x-0 bottom-0 top-[68px] z-40 overflow-y-auto bg-ink lg:hidden" data-id="element-196" data-mp="14agehc49zv63">
          <nav aria-label="Mobile" className="flex flex-col gap-1 px-5 py-6" data-id="element-197" data-mp="3fze9hdty19qd">
            <Link to="/about" className="px-1 py-3 text-[17px] text-white" data-id="element-198" data-mp="3kutinsh0ahgg">
              About us
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-199" data-mp="3ptd6jyf5u3bg" />
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-200" data-mp="3txclwdopew38">Services</p>
            {SERVICE_LINES.map(line => <Link key={line.id} to={`/services/${line.id}`} className="px-1 py-3 text-[17px] text-white" data-id="element-201" data-mp="2x4z9plzt5d1h">
                {line.name}
              </Link>)}
            <Link to="/government" className="px-1 py-3 text-[17px] text-white" data-id="element-202" data-mp="365xgud5jm0oa">
              Cavalla Public Delivery
            </Link>
            <Link to="/augmented-intelligence" className="px-1 py-3 text-[17px] text-white" data-id="element-203" data-mp="2egapl295fbqr">
              Our method
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-204" data-mp="2iguni81jqjdy" />
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-205" data-mp="2ifjkwbju7hof">Insights</p>
            {INSIGHT_LINKS.map(link => <Link key={link.to} to={link.to} className="px-1 py-3 text-[17px] text-white" data-id="element-206" data-mp="25uhvr06rhja1">
                {link.label}
              </Link>)}
            <div className="my-3 h-px bg-white/15" data-id="element-207" data-mp="2ew92m8d1fw5h" />
            <Link to="/portal/login" className="px-1 py-3 text-[17px] text-white" data-id="element-208" data-mp="2evzbuik65kyq">
              Client login
            </Link>
            <Button to="/contact" className="mt-5 w-full" size="lg" shape="pill" data-id="element-209" data-mp="1i4f7yw9vqzh8">
              Book a discovery call
            </Button>
          </nav>
        </div>}
    </header>;
}